import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    
    // Use event delegation for dynamic content (cross-sell & upsell buttons)
    this.addEventListener('click', this.#handleComponentClick.bind(this));
  }

  /**
   * Handles clicks within the component using delegation.
   * @param {Event} event - The click event.
   */
  #handleComponentClick(event) {
    // Cross-sell button
    const crossSellButton = event.target.closest('.cart-crosssell__add-button');
    if (crossSellButton) {
      this.#handleCrossSellAddToCart(event, crossSellButton);
      return;
    }

    // Upsell buttons
    const upsellButton = event.target.closest('[data-cart-upsell-button]');
    if (upsellButton) {
      this.#handleUpsellClick(event, upsellButton);
      return;
    }

    // Quantity promo badge (empty state click)
    const qtyPromoBadge = event.target.closest('[data-qty-promo-badge]');
    if (qtyPromoBadge) {
      this.#handleQtyPromoBadgeClick(event, qtyPromoBadge);
      return;
    }
  }

  /**
   * Tier 1 → tier 2 upgrade. Keeps subscription vs one-time.
   * @param {Event} event
   * @param {HTMLButtonElement} badge
   */
  async #handleQtyPromoBadgeClick(event, badge) {
    event.preventDefault();

    if (badge.classList.contains('loading')) return;

    const lineIndex = parseInt(badge.dataset.lineIndex || '0', 10);
    const currentQty = parseInt(badge.dataset.currentQty || '1', 10);
    const tier2VariantId = badge.dataset.tier2VariantId || '';
    const tier2PlanId = badge.dataset.tier2PlanId || '';
    const isSubscription = badge.dataset.isSubscription === 'true';

    if (lineIndex < 1 || !tier2VariantId) return;

    const targetPlanId = isSubscription ? tier2PlanId : '';

    badge.classList.add('loading');

    try {
      await this.#removeCartItem(lineIndex);

      const qty = Math.max(currentQty, 1);
      const bundleItems =
        isSubscription && targetPlanId
          ? this.#buildSubscriptionBundleItems(tier2VariantId, targetPlanId, qty, '2')
          : null;

      if (bundleItems) {
        await this.#addCartItems(bundleItems);
      } else {
        await this.#addCartItem(tier2VariantId, targetPlanId, qty);
      }

      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { source: 'qty-promo-upgrade' } },
        })
      );
    } catch (error) {
      console.error('[QtyPromo] Upgrade failed:', error);
      badge.classList.remove('loading');
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  }

  /**
   * Handles the upsell button click (replacing an item).
   * @param {Event} event - The click event.
   * @param {HTMLElement} button - The button element.
   */
  async #handleUpsellClick(event, button) {
    event.preventDefault();
    
    if (button.disabled || button.classList.contains('loading')) return;

    const lineIndex = parseInt(button.dataset.lineIndex, 10);
    const targetVariantId = button.dataset.targetVariant;
    const targetPlanId = button.dataset.targetPlan || '';
    const itemQuantity = parseInt(button.dataset.itemQuantity, 10);

    if (!lineIndex || !targetVariantId) {
      console.error('Missing required data attributes');
      return;
    }

    // Disable button and show loading state
    button.disabled = true;
    button.classList.add('loading');

    try {
      await this.#removeCartItem(lineIndex);

      const config = this.#readSubscriptionBonusConfig();
      const tier = this.#tierForVariant(targetVariantId, config);
      const bundleItems =
        targetPlanId && tier
          ? this.#buildSubscriptionBundleItems(targetVariantId, targetPlanId, itemQuantity, tier)
          : null;

      if (bundleItems) {
        await this.#addCartItems(bundleItems);
      } else {
        await this.#addCartItem(targetVariantId, targetPlanId, itemQuantity);
      }

      await sectionRenderer.renderSection(this.sectionId, { cache: false });

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { source: 'cart-upsell-replace' } },
        })
      );

    } catch (error) {
      console.error('Error replacing cart item:', error);
      
      // Re-enable button on error
      button.disabled = false;
      button.classList.remove('loading');
      
      // Refresh cart to restore correct state
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  }

  /**
   * @returns {object|null}
   */
  #readSubscriptionBonusConfig() {
    const el = document.getElementById('SubscriptionBonusConfig');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  #createBundleId() {
    return 'pc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
  }

  /**
   * Build main + nested bonus lines for a subscription tier (matches plan-cards ATC).
   * @param {string} variantId
   * @param {string} planId
   * @param {number} quantity
   * @param {string} tier
   * @returns {Array<object>|null}
   */
  #buildSubscriptionBundleItems(variantId, planId, quantity, tier) {
    const config = this.#readSubscriptionBonusConfig();
    if (!config?.enabled) return null;

    const tierBonuses = config[tier] || [];
    if (!tierBonuses.length) return null;

    const bundleId = this.#createBundleId();
    const mainId = parseInt(variantId, 10);
    const matchMainQty = config.matchMainQuantity !== false;

    /** @type {Array<object>} */
    const items = [
      {
        id: mainId,
        quantity,
        selling_plan: parseInt(planId, 10),
        properties: {
          _pc_bundle_id: bundleId,
          _pc_bundle_role: 'main',
        },
      },
    ];

    tierBonuses.forEach((bonus) => {
      if (!bonus?.variantId) return;
      const baseQty = parseInt(bonus.quantity, 10) || 1;
      const bonusQty = matchMainQty ? baseQty * quantity : baseQty;
      items.push({
        id: parseInt(bonus.variantId, 10),
        quantity: bonusQty,
        parent_id: mainId,
        properties: {
          _pc_bundle_id: bundleId,
          _pc_bundle_role: 'bonus',
        },
      });
    });

    return items.length > 1 ? items : null;
  }

  /**
   * @param {string} variantId
   * @param {object|null} config
   * @returns {string|null}
   */
  #tierForVariant(variantId, config) {
    return config?.tierVariants?.[String(variantId)] || null;
  }

  /**
   * Remove a cart item by setting its quantity to 0
   * @param {number} lineIndex - The line index to remove
   */
  async #removeCartItem(lineIndex) {
    const body = JSON.stringify({
      line: lineIndex,
      quantity: 0,
      sections: this.sectionId,
      sections_url: window.location.pathname,
    });

    const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body
    });
    
    if (!response.ok) {
      throw new Error('Failed to remove cart item');
    }

    return response.json();
  }

  /**
   * Add a new item to the cart with variant and optional selling plan
   * @param {string} variantId - The variant ID
   * @param {string} planId - The selling plan ID (optional)
   * @param {number} quantity - The quantity
   */
  async #addCartItem(variantId, planId, quantity) {
    const items = [{
      id: parseInt(variantId, 10),
      quantity: quantity,
    }];

    // Add selling plan if provided
    if (planId && planId.trim() !== '') {
      items[0].selling_plan = parseInt(planId, 10);
    }

    const body = JSON.stringify({
      items: items,
      sections: this.sectionId,
      sections_url: window.location.pathname,
    });

    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add cart item');
    }

    return response.json();
  }

  /**
   * Add multiple cart lines in one request (subscription bundle).
   * @param {Array<object>} items
   */
  async #addCartItems(items) {
    const body = JSON.stringify({
      items,
      sections: this.sectionId,
      sections_url: window.location.pathname,
    });

    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add cart items');
    }

    return response.json();
  }
  
  /**
   * Handles the cross-sell Add to Cart button click.
   * @param {Event} event - The click event.
   * @param {HTMLElement} button - The button element.
   */
  async #handleCrossSellAddToCart(event, button) {
    // If button is not passed (legacy call), try to get it from currentTarget
    const btn = button || /** @type {HTMLButtonElement} */ (event.currentTarget);
    const variantId = btn.dataset.variantId;
    
    if (!variantId) return;
    
    btn.disabled = true;
    btn.classList.add('loading');
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }]
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      
      // Show success checkmark
      btn.classList.remove('loading');
      btn.classList.add('success');
      
      // Wait a moment to show the checkmark, then refresh
      setTimeout(async () => {
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
      }, 800);
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      btn.classList.remove('loading');
      btn.disabled = false;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Finds a cart row by Shopify line number (1-based), regardless of display order.
   * @param {number} line
   * @returns {HTMLTableRowElement|undefined}
   */
  #getCartLineRow(line) {
    const rows = this.refs.cartItemRows;
    if (!rows?.length) return undefined;

    const lineNumber = String(line);
    return rows.find((row) => row.dataset.cartLine === lineNumber);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (window.__pilotCartRemovalPending) return;
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getCartLineRow(line);

    if (!lineItemRow) return;

    const textComponents = /** @type {NodeListOf<TextComponent>} */ (lineItemRow.querySelectorAll('text-component'));
    textComponents.forEach(component => component.shimmer());
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    if (window.__pilotCartRemovalPending) return;
    return this.updateQuantity({line, quantity: 0, action: 'clear'});
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  // pilot-cart-removal-v1: mutation, redraw and cleanup share one lifecycle.
  async updateQuantity(config) {
    if (window.__pilotCartRemovalPending) return;
    const controller = new AbortController();
    window.__pilotCartRemovalPending = controller;
    const sectionId = this.dataset.sectionId;
    const snapshot = document.getElementById('shopify-section-' + sectionId)?.outerHTML;
    document.querySelectorAll('[data-pilot-cart-error]').forEach(el => el.remove());
    let timer;
    try {
      await Promise.race([
        this.#performQuantityUpdate(config, controller.signal),
        new Promise((_, reject) => { timer = setTimeout(() => {
          controller.abort();
          reject(new Error('Cart update timed out'));
        }, 15000); })
      ]);
    } catch (error) {
      controller.abort();
      console.error(error);
      // Read-only recovery. Never replay an uncertain mutation.
      try {
        await sectionRenderer.renderSection(sectionId, { cache: false });
      } catch (refreshError) {
        console.error(refreshError);
        if (snapshot) await morphSection(sectionId, snapshot).catch(console.error);
      }
      const host = [...document.querySelectorAll('cart-items-component')].find(el => el.dataset.sectionId === sectionId) || this;
      const message = typeof Theme !== 'undefined' && Theme.translations?.cart_update_error;
      if (message) {
        const alert = document.createElement('p');
        alert.dataset.pilotCartError = '';
        alert.setAttribute('role', 'alert');
        alert.textContent = message;
        host.prepend(alert);
      }
    } finally {
      clearTimeout(timer);
      this.#enableCartItems();
      resetShimmer(this);
      // Redraw can replace the host. Release the live drawer, including Safari repaint.
      document.querySelectorAll('cart-items-component').forEach(el => {
        el.classList.remove('cart-items-disabled');
        resetShimmer(el);
      });
      document.querySelectorAll('.cart-drawer__dialog').forEach(el => {
        el.classList.remove('cart-drawer--updating');
        void el.offsetWidth;
      });
      if (window.__pilotCartRemovalPending === controller) window.__pilotCartRemovalPending = null;
    }
  }

  #performQuantityUpdate(config, signal) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sectionIds = Array.from(sectionsToUpdate);
    const lineItemRow = this.#getCartLineRow(line);
    const manager = window.__subscriptionBundleManager;
    const stampGiftManager = window.__stampGiftManager;
    const useBundleQtySync =
      lineItemRow?.dataset?.pcBundleRole === 'main' &&
      manager?.willSyncBundleQuantity?.() &&
      !manager?.isBusy;
    const useStampGiftSync =
      lineItemRow?.dataset?.stampGiftRole === 'main' &&
      stampGiftManager?.willSyncStampGiftQuantity?.() &&
      !stampGiftManager?.isBusy;

    cartTotal?.shimmer();

    const applyCartResponse = async (parsedResponseText) => {
      signal.throwIfAborted();
      resetShimmer(this);

      if (parsedResponseText.errors) {
        throw new Error(typeof parsedResponseText.errors === 'string' ? parsedResponseText.errors : 'Cart update rejected');
      }

      const sectionHtml = parsedResponseText.sections?.[this.sectionId];
      const newSectionHTML = sectionHtml
        ? new DOMParser().parseFromString(sectionHtml, 'text/html')
        : null;

      const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections || {},
        })
      );

      if (sectionHtml) {
        await morphSection(this.sectionId, sectionHtml);
      }

      if (!sectionHtml) await sectionRenderer.renderSection(this.sectionId, { cache: false });

      this.#updateProgressBarAndShipping(
        parsedResponseText.total_price,
        parsedResponseText.cart_level_discount_applications || [],
        parsedResponseText.items || []
      );
    };

    const finish = () => {
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    };

    if (useBundleQtySync) {
      return manager
        .syncMainLineQuantity(line, quantity, sectionIds, lineItemRow?.dataset?.key)
        .then((parsedResponseText) => {
          if (!parsedResponseText) {
            return this.#fallbackLineQuantityChange(line, quantity, sectionIds, signal, lineItemRow?.dataset?.key).then(applyCartResponse);
          }
          return applyCartResponse(parsedResponseText);
        })
        .finally(finish);
    }

    if (useStampGiftSync) {
      return stampGiftManager
        .syncMainLineQuantity(line, quantity, sectionIds, lineItemRow?.dataset?.key)
        .then((parsedResponseText) => {
          if (!parsedResponseText) {
            return this.#fallbackLineQuantityChange(line, quantity, sectionIds, signal, lineItemRow?.dataset?.key).then(applyCartResponse);
          }
          return applyCartResponse(parsedResponseText);
        })
        .finally(finish);
    }

    return this.#fallbackLineQuantityChange(line, quantity, sectionIds, signal, lineItemRow?.dataset?.key)
      .then(applyCartResponse)
      .finally(finish);
  }

  /**
   * Single-line cart/change.js (non-bundle lines).
   * @param {number} line
   * @param {number} quantity
   * @param {string[]} sectionIds
   */
  async #fallbackLineQuantityChange(line, quantity, sectionIds, signal, key) {
    signal.throwIfAborted();
    if (!key) throw new Error('Cart line no longer available');
    const body = JSON.stringify({ id: key, quantity, sections: sectionIds.join(','), sections_url: window.location.pathname });
    const response = await fetch(Theme.routes.cart_change_url, { ...fetchConfig('json', { body }), signal });
    const parsed = await response.json();
    if (!response.ok) throw new Error(parsed.description || parsed.message || 'Cart update rejected');
    return parsed;
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const lineItemRow = this.#getCartLineRow(line);
    const quantityInput = lineItemRow?.querySelector('input[data-cart-line]');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Stores the last cart data for discount code detection.
   * @type {Object|null}
   */
  #lastCartData = null;

  /**
   * Checks if any applied discount code grants free shipping.
   * Checks both cart-level AND line-level (product) discounts.
   * @param {Array<{title: string}>} cartLevelDiscounts - The cart's cart-level discount applications.
   * @param {Array<{line_level_discount_allocations: Array<{discount_application: {title: string}}>}>} [items] - The cart items with line-level discounts.
   * @returns {boolean} True if a free shipping code is applied.
   */
  #hasFreeShippingCode(cartLevelDiscounts = [], items = []) {
    const freeShippingCodesRaw = this.dataset.freeShippingCodes || '';
    
    if (!freeShippingCodesRaw.trim()) {
      return false;
    }
    
    const freeShippingCodes = freeShippingCodesRaw
      .split(',')
      .map(code => code.trim().toLowerCase())
      .filter(code => code.length > 0);
    
    if (freeShippingCodes.length === 0) {
      return false;
    }
    
    // Check cart-level discounts
    const hasCartLevelMatch = cartLevelDiscounts.some(discount => {
      const discountTitle = (discount.title || '').toLowerCase().trim();
      return freeShippingCodes.includes(discountTitle);
    });
    
    if (hasCartLevelMatch) {
      return true;
    }
    
    // Check line-level discounts (discounts applied to products)
    for (const item of items) {
      const allocations = item.line_level_discount_allocations || [];
      for (const allocation of allocations) {
        const discountTitle = (allocation.discount_application?.title || '').toLowerCase().trim();
        if (freeShippingCodes.includes(discountTitle)) {
          return true;
        }
      }
    }
    
    return false;
  }

  /**
   * Updates the progress bar and shipping text client-side.
   * This fixes mobile Safari issues where Shopify's Section Rendering API
   * returns cached/stale cart data during morphing.
   * @param {number} cartTotalCents - The cart total in cents from the API response.
   * @param {Array<{title: string}>} [cartLevelDiscounts] - The cart's cart-level discount applications.
   * @param {Array<Object>} [items] - The cart items (for line-level discount detection).
   */
  #updateProgressBarAndShipping(cartTotalCents, cartLevelDiscounts = [], items = []) {
    // Get settings from data attributes (set in Liquid)
    const thresholdCents = parseInt(this.dataset.shippingThreshold || '6900', 10);
    const bgComplete = this.dataset.bgComplete || '#DAECE3';
    const bgIncomplete = this.dataset.bgIncomplete || '#f0f1f3';
    const textComplete = this.dataset.textComplete || Theme.translations.cart_progress_complete;
    const textIncomplete = this.dataset.textIncomplete || Theme.translations.cart_progress_incomplete;
    const freeShippingText = this.dataset.shippingFreeText || Theme.translations.cart_free_shipping;
    const calculatedAtCheckoutText = this.dataset.shippingCalculatedText || Theme.translations.cart_calculated_at_checkout;
    const amountColor = this.dataset.amountColor || '#DD0605';
    const amountFontWeight = this.dataset.amountFontWeight || '700';
    const highlightColor = this.dataset.highlightColor || '#007e40';
    const highlightFontWeight = this.dataset.highlightFontWeight || '700';
    const currency = this.dataset.currency || 'EUR';
    const subscriptionFreeShipping = this.dataset.subscriptionFreeShipping !== 'false';
    const hideBarOneTime = this.dataset.hideBarOneTime !== 'false';

    const hasSubscriptionItem = items.some(
      (item) => item.selling_plan_allocation != null && item.selling_plan_allocation !== undefined
    );
    
    // Check if a free shipping discount code is applied (cart-level OR line-level)
    const hasFreeShippingCode = this.#hasFreeShippingCode(cartLevelDiscounts, items);
    
    // Calculate amount left (override if free shipping code is applied)
    let amountLeftCents = thresholdCents - cartTotalCents;
    let progressPercentage = Math.min((cartTotalCents / thresholdCents) * 100, 100);
    
    if (subscriptionFreeShipping && hasSubscriptionItem) {
      amountLeftCents = -1;
      progressPercentage = 100;
    } else if (hasFreeShippingCode) {
      amountLeftCents = -1;
      progressPercentage = 100;
    } else if (subscriptionFreeShipping && !hasSubscriptionItem) {
      // One-time carts do not earn free shipping via the dollar threshold
      if (amountLeftCents <= 0) {
        amountLeftCents = 1;
      }
    }

    const progressBar = /** @type {HTMLElement} */ (document.querySelector('.cart-progress-bar'));
    if (progressBar) {
      if (hideBarOneTime && !hasSubscriptionItem && !hasFreeShippingCode) {
        progressBar.style.display = 'none';
      } else {
        progressBar.style.display = '';
      }
    }
    
    // Update progress bar text
    const progressBarText = document.querySelector('.cart-progress-bar__text');
    if (progressBarText && progressBar?.style.display !== 'none') {
      let textTemplate = amountLeftCents <= 0 ? textComplete : textIncomplete;
      
      // Format amount left as money with proper EUR formatting (€45,00)
      let amountLeftFormatted;
      if (currency === 'EUR') {
        // EUR format: €45,00 (euro sign before, comma for decimals)
        amountLeftFormatted = (amountLeftCents / 100).toLocaleString('de-DE', {
          style: 'currency',
          currency: 'EUR'
        });
      } else {
        amountLeftFormatted = (amountLeftCents / 100).toLocaleString(undefined, {
          style: 'currency',
          currency: currency
        });
      }
      
      // Create amount span with color and font weight
      const amountSpan = `<span class="progress-bar-amount" style="color: ${amountColor}; font-weight: ${amountFontWeight};">${amountLeftFormatted}</span>`;
      
      // Replace [amount_left] placeholder
      let formattedText = textTemplate.replace('[amount_left]', amountSpan);
      
      // Replace ##text## with highlighted spans
      formattedText = formattedText.replace(/##([^#]+)##/g, `<span class="progress-bar-highlight" style="color: ${highlightColor}; font-weight: ${highlightFontWeight};">$1</span>`);
      
      // Use innerHTML to render the spans
      progressBarText.innerHTML = formattedText;
    }
    
    // Update progress bar fill
    const progressBarFill = /** @type {HTMLElement} */ (document.querySelector('.cart-progress-bar__fill'));
    if (progressBarFill) {
      progressBarFill.style.setProperty('--progress', `${progressPercentage}%`);
    }

    // Mirror the value onto the container so the shipping icon, which is a
    // sibling of the fill, can position itself along the bar.
    if (progressBar) {
      progressBar.style.setProperty('--progress', `${progressPercentage}%`);
    }
    
    // Update progress bar background color using theme settings
    if (progressBar && progressBar.style.display !== 'none') {
      progressBar.style.backgroundColor = amountLeftCents <= 0 ? bgComplete : bgIncomplete;
    }
    
    // Update shipping text in order summary
    const shippingValue = document.querySelector('[data-cart-shipping-value]');
    if (shippingValue) {
      if (amountLeftCents <= 0) {
        shippingValue.textContent = freeShippingText;
        shippingValue.classList.add('cart-order-summary__value--highlight');
      } else {
        shippingValue.textContent = calculatedAtCheckoutText;
        shippingValue.classList.remove('cart-order-summary__value--highlight');
      }
    }
    
    // Restore checkout button icon after morphing
    this.#restoreCheckoutButton();
  }
  
  /**
   * Restores the checkout button icon after morphing.
   * This fixes the issue where morphing replaces the button with wrong HTML.
   */
  #restoreCheckoutButton() {
    const showIcon = this.dataset.checkoutShowIcon === 'true';
    const iconType = this.dataset.checkoutIconType || 'lock';
    const iconSize = this.dataset.checkoutIconSize || '18';
    
    const checkoutButton = document.querySelector('.cart__checkout-button');
    if (!checkoutButton) return;
    
    // Get the button content container
    let buttonContent = checkoutButton.querySelector('.cart__checkout-button-content');
    
    // If the content wrapper doesn't exist, create it
    if (!buttonContent) {
      const buttonText = checkoutButton.textContent.trim();
      checkoutButton.innerHTML = '';
      buttonContent = document.createElement('span');
      buttonContent.className = 'cart__checkout-button-content';
      
      const textSpan = document.createElement('span');
      textSpan.className = 'cart__checkout-button-text';
      textSpan.textContent = buttonText;
      buttonContent.appendChild(textSpan);
      
      checkoutButton.appendChild(buttonContent);
    }
    
    // Check if icon already exists
    let iconSpan = buttonContent.querySelector('.cart__checkout-button-icon');
    
    if (showIcon) {
      // Create icon if it doesn't exist
      if (!iconSpan) {
        iconSpan = document.createElement('span');
        iconSpan.className = 'cart__checkout-button-icon';
        buttonContent.appendChild(iconSpan);
      }
      
      // Set the lock icon SVG (default)
      if (iconType === 'lock') {
        iconSpan.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
        </svg>`;
      }
    } else if (iconSpan) {
      // Remove icon if showIcon is false
      iconSpan.remove();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
